import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coins, ShoppingCart, Zap, Check } from 'lucide-react';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './PurchaseCoins.css';

function PurchaseCoins() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isGerman = i18n.language === 'de';
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadPackages();
    loadSubscriptionStatus();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/packages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setPackages(data);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const handlePurchase = (pkg) => {
    // Navigate to unified checkout with package data
    const totalCoins = pkg.coins + pkg.bonus_coins;
    navigate('/checkout', { 
      state: { 
        orderDetails: {
          order_type: 'coins',
          package: pkg,
          planName: `${totalCoins} Coins`,
          amount: pkg.price_eur,
          package_id: pkg.id
        }
      } 
    });
  };

  const getPackageColor = (index) => {
    const colors = [
      { border: 'rgba(194, 164, 90, 0.3)', bg: 'rgba(194, 164, 90, 0.05)' },
      { border: 'rgba(194, 164, 90, 0.4)', bg: 'rgba(194, 164, 90, 0.08)' },
      { border: 'rgba(194, 164, 90, 0.5)', bg: 'rgba(194, 164, 90, 0.1)' },
      { border: 'rgba(194, 164, 90, 0.6)', bg: 'rgba(194, 164, 90, 0.12)' },
      { border: 'rgba(194, 164, 90, 0.8)', bg: 'rgba(194, 164, 90, 0.15)' }
    ];
    return colors[index % colors.length];
  };

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="purchase-coins-page">
        <div className="purchase-coins-container">
          <div className="purchase-header">
            <div className="purchase-header-icon">
              <Coins size={48} />
            </div>
            <h1>{t('coins.title')}</h1>
            <p>{t('coins.subtitle')}</p>
          </div>

          {loading ? (
            <div className="loading-spinner">{t('common.loading')}</div>
          ) : (
            <div className="packages-grid">
              {packages.map((pkg, index) => {
                const totalCoins = pkg.coins + pkg.bonus_coins;
                const hasBonus = pkg.bonus_coins > 0;
                const colors = getPackageColor(index);
                
                return (
                  <div
                    key={pkg._id}
                    className="package-card"
                    style={{
                      borderColor: colors.border,
                      background: colors.bg
                    }}
                    data-testid={`package-${pkg.coins}`}
                  >
                    {hasBonus && (
                      <div className="package-badge">
                        <Zap size={14} />
                        <span>+{pkg.bonus_coins} Bonus</span>
                      </div>
                    )}
                    
                    <div className="package-icon">
                      <Coins size={40} />
                    </div>
                    
                    <div className="package-coins">
                      <span className="coins-amount">{totalCoins}</span>
                      <span className="coins-label">Coins</span>
                    </div>
                    
                    {hasBonus && (
                      <div className="package-breakdown">
                        <span>{pkg.coins} + {pkg.bonus_coins} Bonus</span>
                      </div>
                    )}
                    
                    {pkg.description && (
                      <div className="package-description">{pkg.description}</div>
                    )}
                    
                    <div className="package-price">
                      <span className="price-amount">{pkg.price_eur.toFixed(2)}€</span>
                      <span className="price-per-coin">
                        ({(pkg.price_eur / totalCoins).toFixed(2)}€ / Coin)
                      </span>
                    </div>
                    
                    <button
                      className="package-buy-btn"
                      onClick={() => handlePurchase(pkg)}
                      data-testid={`buy-${pkg.coins}`}
                    >
                      <ShoppingCart size={18} />
                      {t('coins.buyNow')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="purchase-info-section">
            <div className="info-card">
              <div className="info-icon">
                <Check size={24} />
              </div>
              <div className="info-content">
                <h3>{isGerman ? 'Permanente Credits' : 'Permanent Credits'}</h3>
                <p>{isGerman ? 'Gekaufte Coins verfallen nie und bleiben auf deinem Konto' : 'Purchased coins never expire and stay on your account'}</p>
              </div>
            </div>
            
            <div className="info-card">
              <div className="info-icon">
                <Coins size={24} />
              </div>
              <div className="info-content">
                <h3>Flexible Verwendung</h3>
                <p>Nutze Coins für KI-Chat, Programme und weitere Features</p>
              </div>
            </div>
            
            <div className="info-card">
              <div className="info-icon">
                <Zap size={24} />
              </div>
              <div className="info-content">
                <h3>Bonus bei größeren Paketen</h3>
                <p>Je mehr du kaufst, desto mehr Bonus-Coins erhältst du</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PurchaseCoins;
